// posRealtime.js
//
// The POS's one SignalR connection. Screens subscribe to the events they care
// about; the connection is opened by the first subscriber and closed when the
// last one leaves, so there is never more than one socket however many screens
// are listening.
//
// It owns the connection lifecycle and nothing else — it never touches data.
// A subscriber is told two things:
//   events[name](payload)  the server says something changed. The payload
//                          carries ids only, never rows.
//   onResync(why)          you may have missed events (first connect,
//                          reconnect): reload EVERYTHING you show.
//
// Rules this encodes (each one a real failure mode):
//   - every event handler is registered BEFORE start(), so nothing between
//     start and subscribe is lost;
//   - the reconnect policy never gives up (the default stops after ~4 tries and
//     strands a tablet that slept);
//   - if the very first start() fails, or the connection closes for good, it
//     keeps trying rather than staying dead.
//
// Where the hub lives: REACT_APP_POS_REALTIME_URL (.env.development while the
// hub is the standalone SignalRTest server). With it unset the whole feature is
// off — subscribe() does nothing and screens behave exactly as they did before.
// Once the hub is merged into the main backend this becomes
// axios.defaults.baseURL.

import * as signalR from '@microsoft/signalr';

export const REALTIME_URL = (process.env.REACT_APP_POS_REALTIME_URL || '').replace(/\/+$/, '');

// Same fallback the order screens save with when the user has no branch.
const FALLBACK_BRANCH = '00000000-0000-0000-0000-000000000001';

// The branch this tablet works in: the outlet picked in the topbar, else the
// user's default branch — the same order RestaurantOrder._resolveBranchId uses,
// so a tablet listens on the branch its own orders are saved under.
export const resolveBranchId = (state) => (
    (state.master && state.master.selectedBranch)
    || (state.auth && state.auth.userObject && state.auth.userObject.defaultbranch)
    || FALLBACK_BRANCH
);

const EVENTS = ['kotChanged', 'menuQtyChanged'];

const RETRY_MS = [0, 2000, 5000, 10000];
const nextDelay = (ctx) => (
    RETRY_MS[ctx.previousRetryCount] !== undefined ? RETRY_MS[ctx.previousRetryCount] : 30000
);
const RESTART_MS = 5000;

const subscribers = new Set();
let conn = null;
let connected = false;
let timer = null;
let running = false;

const each = (fn) => subscribers.forEach((s) => {
    try { fn(s); } catch (e) { console.error('posRealtime subscriber failed', e); }
});

const connect = async (url) => {
    if (!running) return;

    const c = new signalR.HubConnectionBuilder()
        .withUrl(url)
        .withAutomaticReconnect({ nextRetryDelayInMilliseconds: nextDelay })
        .configureLogging(signalR.LogLevel.Warning)
        .build();
    conn = c;

    EVENTS.forEach((name) => {
        c.on(name, (p) => each((s) => s.events && s.events[name] && s.events[name](p)));
    });
    c.onreconnecting(() => { connected = false; });
    c.onreconnected(() => {
        connected = true;
        each((s) => s.onResync && s.onResync('reconnected'));
    });
    c.onclose(() => {
        connected = false;
        if (running && conn === c) timer = setTimeout(() => connect(url), RESTART_MS);
    });

    try {
        await c.start();
    } catch (e) {
        if (running && conn === c) timer = setTimeout(() => connect(url), RESTART_MS);
        return;
    }
    if (!running || conn !== c) { c.stop(); return; }
    connected = true;
    each((s) => s.onResync && s.onResync('connected'));
};

// subscribe({ companyId, branchId, events: { kotChanged: fn, ... }, onResync }) -> unsubscribe()
export function subscribe(subscriber) {
    if (!REALTIME_URL || !subscriber || !subscriber.companyId || !subscriber.branchId) return () => {};

    subscribers.add(subscriber);
    if (!running) {
        running = true;
        connect(REALTIME_URL + '/hubs/pos?companyId=' + encodeURIComponent(subscriber.companyId)
            + '&branchId=' + encodeURIComponent(subscriber.branchId));
    } else if (connected && subscriber.onResync) {
        // Joined an already-open connection: it has seen nothing yet.
        subscriber.onResync('connected');
    }

    return () => {
        subscribers.delete(subscriber);
        if (subscribers.size > 0) return;
        running = false;
        connected = false;
        clearTimeout(timer);
        if (conn) { const c = conn; conn = null; c.stop(); }
    };
}
